import { Fetchable } from '#lib/util/fetchable';

import { getChatContext } from '#src/contexts/chat.context.svelte.ts';
import { ragService } from '#src/services/rag-service.svelte.ts';
import type { DocumentDTO, DocumentOverview } from '#src/types/documents.ts';
import type { CitedSource } from '#src/types/sources.ts';
import { getContext, setContext } from 'svelte';

export type SelectedItem = {
	document?: DocumentDTO;
	source?: CitedSource;
};

type Selection =
	{ type: 'hash'; hash: string } | { type: 'doc'; docId: number } | { type: 'web'; webId: string };

export class DocumentContext {
	private fetchedDocument: DocumentDTO | null = $state(null);

	documents: DocumentOverview[] = $state([]);
	kbSources: CitedSource[] = $state([]);
	secondarySources: CitedSource[] = $state([]);

	selection: Selection | null = $state(null);

	readonly fetchable = new Fetchable();

	private chatCtx!: ReturnType<typeof getChatContext>;

	readonly selectedCitationHash = $derived(
		this.selection?.type === 'hash' ? this.selection.hash : null
	);

	readonly selected: SelectedItem | null = $derived.by(() => {
		const sel = this.selection;
		if (!sel) {
			return null;
		}
		if (sel.type === 'web') {
			const source = this.secondarySources.find((s) => s.web_id === sel.webId);
			return source ? { source } : null;
		}
		if (sel.type === 'hash') {
			const info = this.chatCtx?.getCitationInfo(sel.hash);
			if (info?.source.web_id != null) {
				const source = this.secondarySources.find((s) => s.web_id === info.source.web_id);
				return source ? { source } : null;
			}
		}
		if (this.fetchedDocument != null) {
			const source = this.kbSources.find((s) => s.doc_id === this.fetchedDocument!.id);
			return { document: this.fetchedDocument, source };
		}
		return null;
	});

	constructor() {
		this.chatCtx = getChatContext();

		$effect(() => {
			const sel = this.selection;
			let docId: number | null = null;
			if (sel?.type === 'doc') {
				docId = sel.docId;
			} else if (sel?.type === 'hash') {
				docId = this.chatCtx?.getCitationInfo(sel.hash)?.source.doc_id ?? null;
			}
			// A cited document belongs to the conversation's instance.
			const instanceId = this.chatCtx?.conversation?.instance;
			if (docId != null && instanceId) {
				this.fetchable.fetch(async () => {
					this.fetchedDocument = await ragService.getDocument(instanceId, docId!);
				});
			} else {
				this.fetchedDocument = null;
			}
		});
	}

	selectByHash(hash: string) {
		this.selection = { type: 'hash', hash };
	}

	selectByDocId(docId: number) {
		this.selection = { type: 'doc', docId };
	}

	selectByWebId(webId: string) {
		this.selection = { type: 'web', webId };
	}

	clearSelection() {
		this.selection = null;
	}
}

export const DOCUMENTS_CONTEXT_KEY = Symbol('documents');

export function getDocumentContext() {
	return getContext<DocumentContext>(DOCUMENTS_CONTEXT_KEY);
}

export function setDocumentContext(ctx: DocumentContext) {
	setContext(DOCUMENTS_CONTEXT_KEY, ctx);
	return ctx;
}
